import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import UtilizationContentBarPercent from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/ContentBarPercent';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { PAGINATION_CONSTANTS } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/constants';
import { useMainEntityName, useMainEntityType, useProductEntity, useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import {
  CategoryType,
  ContentMetricType
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import ContentUtilizationModal from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Modals/ContentUtilizationModal';
import { useContentChartSubtitles } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useDynamicSubtitle';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import { ContentSplineChart } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/ContentSplineChart';
import {
  ContentSubTabs,
  createMetricColumnDefinition,
  createProductColumn
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/utils';
import { ContentDropdownBarChart } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/ContentBarChart';
import { ContentTableGridV3 } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/ContentTableGridV3';
import { ProductLevelContentUtilizationSummary } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/ProductLevelContentUtilizationSummary';
import { useContentService } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useContentService';
import { ContentSectionId } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/ContentSectionId';
import { useContentUtilizationStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/stores/ContentUtilizationStore';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  FilterLink,
  useFilterRendering
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useFilterRendering';
import { formatMetric } from 'src/utils/app';

const ContentUtilizationV3Page = () => {
  const entityName = useMainEntityName();
  const {
    getContentScoresByWeek,
    getContentScoresByProduct,
    getContentScoreByCategory,
    getProductLevelContentUtilizationDetails
  } = useContentService();

  const { isProductEntity } = useProductEntity();
  const entity = useAppSelector((state) => state.entityService.mainEntity);

  const { generateBarChartSubtitle } = useContentChartSubtitles();
  const { openModal, resetFilters, filters, getActiveFilters } = useContentUtilizationStore();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeFilters = useMemo(() => getActiveFilters(), [getActiveFilters, filters]);

  const { renderFilters } = useFilterRendering(activeFilters);

  useEffect(() => {
    resetFilters();
  }, [resetFilters]);

  const title = 'Content Utilization';
  const entityType = useMainEntityType();
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const [isLoading, setIsLoading] = useState(false);

  const getSubtitle = useCallback(() => {
    const comparisonPeriodDisplayedName = comparisonTimePeriod.shortDisplayName.toLowerCase();

    if (activeFilters.length > 0) {
      return (
        <Text variant="body1">
          The table displays the content utilization scores for each product with low{' '}
          <span>{renderFilters(activeFilters)}</span> utilization for the last week compared to the{' '}
          {comparisonPeriodDisplayedName}.{' '}
          <FilterLink clickable onClick={openModal}>
            Edit {activeFilters.length === 1 ? 'Filter' : `${activeFilters.length} Filters`}
          </FilterLink>
          .
        </Text>
      );
    }

    return `The table displays the content utilization scores for each product last week compared to the ${comparisonPeriodDisplayedName}.`;
  }, [activeFilters, comparisonTimePeriod.shortDisplayName, renderFilters, openModal]);

  const groupByOptionsByEntityType = {
    default: [
      {
        id: 'category',
        label: 'Category',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Category)
      },
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Subcategory)
      },
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Brand)
      }
    ],
    brand: [
      {
        id: 'category',
        label: 'Category',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Category)
      },
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Subcategory)
      }
    ],
    category: [
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Subcategory)
      },
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Brand)
      }
    ],
    subcategory: [
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Brand)
      }
    ]
  };

  const columnDefinitions = [
    { id: 'contentUtilizationScore', header: 'Content Utilization', opts: { cellStyle: { width: '84px' } } },
    { id: 'titleScore', header: 'Title Score', opts: { cellStyle: { width: '84px' } } },
    { id: 'featureBulletsScore', header: 'Bullet Score', opts: { cellStyle: { width: '84px' } } },
    { id: 'descriptionScore', header: 'Description Score', opts: { cellStyle: { width: '84px' } } },
    { id: 'imagesScore', header: 'Image Score', opts: { cellStyle: { width: '84px' } } },
    { id: 'videosScore', header: 'Video Score', opts: { cellStyle: { width: '84px' } } },
    { id: 'aPlusScore', header: 'Enhanced Score', opts: { cellStyle: { width: '84px' } } }
  ].map(({ id, header, opts }) => createMetricColumnDefinition([id, header], opts));

  const renderBarChart = !isProductEntity && entity.type !== 'subcategory' && entity.type !== 'segment';

  return (
    <BasePageContainer sx={{ display: 'flex', flexDirection: 'column', gap: `${BEACON_CHART_SPACING}px` }}>
      <>
        <UtilizationContentBarPercent forceLoading={isLoading} />
        <ContentSplineChart
          title={`${title} by Week`}
          queryFn={() =>
            getContentScoresByWeek({
              contentMetricType: ContentMetricType.ContentUtilizationScore,
              mainTimePeriod,
              comparisonTimePeriod
            })
          }
          renderSubtitle={(formattedAverage: string, change: number) => {
            const direction = change >= 0 ? 'higher' : 'lower';
            const absChange = formatMetric(Math.abs(change), METRICTYPE.PERCENT, {
              decimalPlaces: 2,
              showFullValue: true
            });
            const entityDisplay = isProductEntity ? 'this product' : entityName;
            return `On average, ${entityDisplay} earned a ${formattedAverage} content utilization score during the ${mainTimePeriod.shortDisplayName
              } period, which is ${absChange} ${direction} compared to the ${comparisonTimePeriod.shortDisplayName.toLowerCase()}.`;
          }}
        />

        {renderBarChart && (
          <ContentDropdownBarChart
            prefix={`${title} by`}
            options={groupByOptionsByEntityType[entityType] || groupByOptionsByEntityType.default}
            renderSubtitle={(selectedOption) => {
              return generateBarChartSubtitle({
                metricName: 'content utilization scores',
                metricType: selectedOption
              });
            }}
          />
        )}
        {entityType === 'product' ? (
          <ProductLevelContentUtilizationSummary
            queryFn={() => getProductLevelContentUtilizationDetails()}
            renderSubtitle={() => {
              return 'This section displays the content utilization scores for every individual element last week.';
            }}
          />
        ) : (
          <div id={ContentSectionId.TableGrid} key={`${ContentSectionId.TableGrid}}`}>
            <ContentTableGridV3
              key={`content-utilization-by-product-${mainTimePeriod.startWeek}-${mainTimePeriod.endWeek
                }-${JSON.stringify(filters)}`}
              title={`${title} by Product`}
              productColumn={createProductColumn('461px')}
              columnDefinitions={columnDefinitions}
              queryKeyOverride={`content-utilization-by-product-${mainTimePeriod.startWeek}-${mainTimePeriod.endWeek
                }-${JSON.stringify(filters)}`}
              queryFn={(page: number) =>
                getContentScoresByProduct(
                  ContentMetricType.ContentUtilizationScore,
                  mainTimePeriod,
                  comparisonTimePeriod,
                  [], // voiceAndToneMatchTypes
                  {
                    filterTitleInaccuracies: false,
                    filterBulletPointsInaccuracies: false,
                    filterImageInaccuracies: false,
                    filterDescriptionInaccuracies: false,
                    filterVideoInaccuracies: false,
                    minimumRequiredTitleUtilizationScore: filters.titleUtilization ? 0.7 : undefined,
                    minimumRequiredBulletPointsUtilizationScore: filters.bulletUtilization ? 0.7 : undefined,
                    minimumRequiredImageUtilizationScore: filters.imageUtilization ? 0.7 : undefined,
                    minimumRequiredDescriptionUtilizationScore: filters.descriptionUtilization ? 0.7 : undefined,
                    minimumRequiredVideoUtilizationScore: filters.videoUtilization ? 0.99 : undefined,
                    minimumRequiredAPlusUtilizationScore: filters.enhancedUtilization ? 0 : undefined
                  },
                  page,
                  PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE
                )
              }
              mode="table"
              subtitle={getSubtitle()}
              filterModal={<ContentUtilizationModal />}
              setOpen={openModal}
              loadingCallback={(isLoading) => {
                setIsLoading(isLoading);
              }}
              isFilterActive={activeFilters.length > 0}
              pageSize={PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE}
              exportType={ContentSubTabs.contentUtilization}
            />
          </div>
        )}
      </>
    </BasePageContainer>
  );
};

export default ContentUtilizationV3Page;
